import { ReactNode } from 'react';
import ReportExportButton from '@/components/reports/ReportExportButton';
import Pagination from '@/components/common/Pagination';
import { PURCHASE_REPORT_PAGE_SIZE } from './types';

interface ReportLayoutProps {
  title?: string;
  subtitle?: string;
  filters?: ReactNode;
  summary?: ReactNode;
  actions?: ReactNode;
  onExport?: () => void;
  exportDisabled?: boolean;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  children: ReactNode;
  page?: number;
  total?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
}

export default function ReportLayout({
  title,
  subtitle,
  filters,
  summary,
  actions,
  onExport,
  exportDisabled,
  isLoading,
  isEmpty,
  emptyMessage = 'No records found for the selected filters',
  children,
  page = 0,
  total = 0,
  pageSize = PURCHASE_REPORT_PAGE_SIZE,
  onPageChange,
}: ReportLayoutProps) {
  return (
    <div className="card overflow-hidden">
      {(title || subtitle || (!filters && (onExport || actions))) && (
        <div className="p-5 border-b border-gray-100 flex flex-wrap items-start justify-between gap-3">
          {(title || subtitle) && (
            <div>
              {title && <h2 className="text-lg font-semibold text-gray-900">{title}</h2>}
              {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
            </div>
          )}
          {!filters && (onExport || actions) && (
            <div className="flex items-center gap-2 shrink-0">
              {actions}
              {onExport && <ReportExportButton onClick={onExport} disabled={exportDisabled} />}
            </div>
          )}
        </div>
      )}

      {filters && (
        <div className="p-5 bg-gray-50/50 border-b border-gray-100 flex flex-wrap items-end justify-between gap-4">
          <div className="flex-1">
            {filters}
          </div>
          {(onExport || actions) && (
            <div className="flex items-center gap-2 shrink-0">
              {actions}
              {onExport && <ReportExportButton onClick={onExport} disabled={exportDisabled} />}
            </div>
          )}
        </div>
      )}
      {summary && <div className="p-5 border-b border-gray-100 bg-white">{summary}</div>}

      <div className="overflow-auto max-h-[520px]">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : isEmpty ? (
          <div className="p-12 text-center text-gray-500">{emptyMessage}</div>
        ) : (
          children
        )}
      </div>

      {!isLoading && !isEmpty && onPageChange && total > 0 && (
        <Pagination page={page} pageSize={pageSize} total={total} onPageChange={onPageChange} />
      )}
    </div>
  );
}
